import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { filterSubscriptions } from '../utils/subscriptionUtils';
import NoSubscriptionView from '../components/subscription/NoSubscriptionView';
import { SubscriptionState } from '../types';
import { ChevronRightIcon, Flower, Apple, Sprout, ChevronDownIcon } from 'lucide-react';
import useBottomNavPadding from '../hooks/useBottomNavPadding';
import { useTranslation } from 'react-i18next';
import ShutdownNotice from '../components/MaintenanceNotice';

type FilterStatus = 'all' | 'active' | 'cancelled';
type FilterService = 'all' | 'flower' | 'fruit' | 'cat_grass';

// Mock data for development
const MOCK_SUBSCRIPTIONS: SubscriptionState[] = [
  {
    id: 'mock-1',
    status: 'active',
    plan: 'premium',
    serviceType: 'flower',
    isGift: false,
    isRecurring: true,
    remainingDeliveries: 5,
    nextDeliveryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // Next week
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
    address: {
      id: 'addr-1',
      street: '123 Flower Street, Apt 4B',
      city: 'Bangkok',
      state: 'Bangkok',
      zipCode: '10110',
      country: 'Thailand'
    }
  },
  {
    id: 'mock-2',
    status: 'active',
    plan: 'standard',
    serviceType: 'fruit',
    isGift: false,
    isRecurring: false,
    remainingDeliveries: 2,
    nextDeliveryDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), // In 3 days
    startDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days ago
    address: {
      id: 'addr-2',
      street: '456 Orchard Road, Unit 12',
      city: 'Phuket',
      state: 'Phuket',
      zipCode: '83000',
      country: 'Thailand'
    }
  },
  {
    id: 'mock-3',
    status: 'cancelled',
    plan: 'standard',
    serviceType: 'flower',
    isGift: false,
    isRecurring: true,
    remainingDeliveries: 0,
    nextDeliveryDate: null,
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days ago
    address: {
      id: 'addr-3',
      street: '789 Garden Lane',
      city: 'Chiang Mai',
      state: 'Chiang Mai',
      zipCode: '50000',
      country: 'Thailand'
    }
  },
  {
    id: 'mock-4',
    status: 'active',
    plan: 'premium',
    serviceType: 'fruit',
    isGift: false,
    isRecurring: false,
    remainingDeliveries: 0, // This will show as pending_renewal
    nextDeliveryDate: null,
    startDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(), // 60 days ago
    address: {
      id: 'addr-4',
      street: '321 Tropical Avenue',
      city: 'Pattaya',
      state: 'Chonburi',
      zipCode: '20150',
      country: 'Thailand'
    }
  },
  {
    id: 'mock-5',
    status: 'paused',
    plan: 'standard',
    serviceType: 'flower',
    isGift: false,
    isRecurring: true,
    remainingDeliveries: 8,
    nextDeliveryDate: null,
    startDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(), // 45 days ago
    address: {
      id: 'addr-5',
      street: '555 Rose Street',
      city: 'Bangkok',
      state: 'Bangkok',
      zipCode: '10400',
      country: 'Thailand'
    }
  }
];

// Enable mock data in development
// To use mock data, add ?mock=true to the URL in development mode
const USE_MOCK_DATA = import.meta.env.DEV && window.location.search.includes('mock=true');

export default function SubscriptionsList() { 
  const navigate = useNavigate();
  const { 
    subscriptions,
    loading,
    fetchSubscriptionData
  } = useSubscription();
  const { t } = useTranslation();
  
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('active');
  const [filterService, setFilterService] = useState<FilterService>('all');
  const [selectedCancelledSubscription, setSelectedCancelledSubscription] = useState<SubscriptionState | null>(null);
  const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
  const initialFetchRef = useRef(false);
  
  // Refs for status filter chips
  const allChipRef = useRef<HTMLButtonElement>(null);
  const activeChipRef = useRef<HTMLButtonElement>(null);
  const cancelledChipRef = useRef<HTMLButtonElement>(null);

  // Fetch all subscriptions on mount
  useEffect(() => {
    if (!initialFetchRef.current) {
      console.log('SubscriptionsList: Fetching subscription data');
      fetchSubscriptionData(undefined, true); // Force refresh to use the all endpoint
      initialFetchRef.current = true;
    }
  }, [fetchSubscriptionData]);

  // Helper function to determine effective status
  const getEffectiveStatus = (subscription: any) => {
    // Check both camelCase and snake_case field names for compatibility
    const remainingCount = subscription.remainingDeliveries || (subscription as any).remaining_deliveries;
    
    if (subscription.status === 'active' && 
        subscription.isRecurring === false && 
        remainingCount === 0) {
      return 'pending_renewal';
    }
    return subscription.status;
  };

  // Use mock data in development when enabled
  const allSubscriptions = USE_MOCK_DATA ? MOCK_SUBSCRIPTIONS : subscriptions;
  
  // Filter out gift subscriptions - only show regular subscriptions
  const regularSubscriptions = allSubscriptions.filter((sub: SubscriptionState) => 
    !sub.isGift && !sub.giftType
  );
  
  // Filter subscriptions
  const { active: activeSubscriptions, cancelled: cancelledSubscriptions } = filterSubscriptions(regularSubscriptions);
  
  const hasActiveSubscription = activeSubscriptions.length > 0 && !loading;
  const noActiveSubscriptionConfirmed = !loading && activeSubscriptions.length === 0;

  // Handle subscription selection
  const handleSubscriptionSelect = (subscriptionId: string) => {
    if (!subscriptionId) return;
    navigate(`/subscriptions/${subscriptionId}`);
  };

  // Get filtered subscriptions based on selected status
  const getFilteredSubscriptions = () => {
    let filtered = regularSubscriptions;
    
    // Filter by status
    switch(filterStatus) {
      case 'active':
        filtered = activeSubscriptions;
        break;
      case 'cancelled':
        filtered = cancelledSubscriptions;
        break;
      case 'all':
      default:
        filtered = regularSubscriptions;
    }
    
    // Filter by service type
    if (filterService !== 'all') {
      filtered = filtered.filter(sub => {
        const serviceType = (sub as any).serviceType || 'flower';
        return serviceType === filterService;
      });
    }
    
    return filtered;
  };

  // Apply bottom padding for the navigation
  useBottomNavPadding();

  // Function to handle status filter change with auto-scroll
  const handleStatusFilterChange = (status: FilterStatus, buttonRef: React.RefObject<HTMLButtonElement>) => {
    setFilterStatus(status);
    
    // Auto-scroll the clicked chip into view
    setTimeout(() => {
      if (buttonRef.current) {
        buttonRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }, 100);
  };

  // Render loading state for initial load (skip if using mock data)
  if (loading && subscriptions.length === 0 && !USE_MOCK_DATA) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-6 bg-white rounded-lg shadow-sm">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('subscriptionsList.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50">
      {/* Shutdown Notice */}
      <ShutdownNotice />
      
      <div className="bg-white pt-6 pb-4 pl-5 shadow-sm border-b border-gray-200">
        <h1 className="text-2xl font-bold text-gray-900">{t('subscriptionsList.pageTitle')}</h1>
        <p className="text-gray-600 mt-1">{t('subscriptionsList.pageSubtitle')}</p>
        
        {/* Filters - Single line layout */}
        <div className="mt-4 flex items-center gap-2 min-w-0">
          {/* Service filter dropdown */}
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setServiceDropdownOpen(!serviceDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-full transition-colors text-sm font-medium whitespace-nowrap border border-black"
            >
              {filterService === 'all' ? (
                t('subscriptionsList.filters.allServices')
              ) : filterService === 'flower' ? (
                <>
                  <Flower className="h-4 w-4 text-amber-400" />
                  {t('subscriptionsList.service.flower')}
                </>
              ) : filterService === 'fruit' ? (
                <>
                  <Apple className="h-4 w-4 text-orange-400" />
                  {t('subscriptionsList.service.fruit')}
                </>
              ) : (
                <>
                  <Sprout className="h-4 w-4 text-green-400" />
                  {t('subscriptionsList.service.cat_grass')}
                </>
              )}
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${serviceDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {serviceDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden">
                <div className="py-2">
                  <button
                    onClick={() => {
                      setFilterService('all');
                      setServiceDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors flex items-center justify-between border-none ${
                      filterService === 'all' ? 'bg-white text-black' : 'bg-white text-gray-700'
                    }`}
                  >
                    <span>{t('subscriptionsList.filters.allServices')}</span>
                    {filterService === 'all' && (
                      <div className="w-2 h-2 bg-black rounded-full"></div>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      setFilterService('flower');
                      setServiceDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors flex items-center justify-between border-none ${
                      filterService === 'flower' ? 'bg-white text-black' : 'bg-white text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Flower className="h-4 w-4 text-amber-400" />
                      <span>{t('subscriptionsList.service.flower')}</span>
                    </div>
                    {filterService === 'flower' && (
                      <div className="w-2 h-2 bg-black rounded-full"></div>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      setFilterService('fruit');
                      setServiceDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors flex items-center justify-between border-none ${
                      filterService === 'fruit' ? 'bg-white text-black' : 'bg-white text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Apple className="h-4 w-4 text-orange-400" />
                      <span>{t('subscriptionsList.service.fruit')}</span>
                    </div>
                    {filterService === 'fruit' && (
                      <div className="w-2 h-2 bg-black rounded-full"></div>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      setFilterService('cat_grass');
                      setServiceDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors flex items-center justify-between border-none ${
                      filterService === 'cat_grass' ? 'bg-white text-black' : 'bg-white text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Sprout className="h-4 w-4 text-green-400" />
                      <span>{t('subscriptionsList.service.cat_grass')}</span>
                    </div>
                    {filterService === 'cat_grass' && (
                      <div className="w-2 h-2 bg-black rounded-full"></div>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Vertical divider */}
          <div className="h-6 w-px bg-gray-300 flex-shrink-0"></div>
          
          {/* Status filter chips - scrollable */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide min-w-0 flex-1 pr-4">
            <button 
              ref={allChipRef}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap flex-shrink-0 ${
                filterStatus === 'all' 
                  ? 'bg-black text-white' 
                  : 'bg-white text-gray-700'
              }`}
              onClick={() => handleStatusFilterChange('all', allChipRef)}
            >
              {t('subscriptionsList.filters.all')}
            </button>
            <button 
              ref={activeChipRef}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap flex-shrink-0 ${
                filterStatus === 'active' 
                  ? 'bg-black text-white' 
                  : 'bg-white text-gray-700'
              }`}
              onClick={() => handleStatusFilterChange('active', activeChipRef)}
            >
              {t('subscriptionsList.filters.active')}
            </button>
            <button 
              ref={cancelledChipRef}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap flex-shrink-0 ${
                filterStatus === 'cancelled' 
                  ? 'bg-black text-white' 
                  : 'bg-white text-gray-700'
              }`}
              onClick={() => handleStatusFilterChange('cancelled', cancelledChipRef)}
            >
              {t('subscriptionsList.filters.cancelled')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 mb-16">
        {/* Subscription list view */}
        {getFilteredSubscriptions().length > 0 ? (
          <div className="space-y-4">
            {getFilteredSubscriptions().map((subscription: SubscriptionState) => {
              const effectiveStatus = getEffectiveStatus(subscription);
              const isActive = effectiveStatus === 'active';
              const isCancelled = effectiveStatus === 'cancelled';
              const isPaused = effectiveStatus === 'paused';
              const isPendingRenewal = effectiveStatus === 'pending_renewal';
              const isPastDue = effectiveStatus === 'past_due';
              const serviceType = (subscription as any).serviceType || 'flower';
              const ServiceIcon = serviceType === 'fruit' ? Apple : serviceType === 'cat_grass' ? Sprout : Flower;
              
              return (
                <div 
                  key={subscription.id} 
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md cursor-pointer transition-all duration-200 overflow-hidden"
                  onClick={() => handleSubscriptionSelect(subscription.id || '')}
                >
                  {/* Section 1: Service Type */}
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            serviceType === 'flower' ? 'bg-amber-400' : 
                            serviceType === 'fruit' ? 'bg-orange-400' : 
                            'bg-green-400'
                          }`}>
                            <ServiceIcon className="h-6 w-6 text-white" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                              {subscription.plan ? `${subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)} Plan` : t('subscriptionsList.defaultTitle')}
                            </h3>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                                isActive ? 'bg-green-100 text-green-700' : 
                                isCancelled ? 'bg-red-100 text-red-700' :
                                isPaused ? 'bg-yellow-100 text-yellow-700' :
                                isPendingRenewal ? 'bg-orange-100 text-orange-700' :
                                isPastDue ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {t(`subscriptionsList.status.${effectiveStatus}`) || effectiveStatus?.charAt(0).toUpperCase() + effectiveStatus?.slice(1) || t('subscriptionsList.status.unknown')}
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-600">
                              {t(`subscriptionsList.type.${serviceType}`)} {t('subscriptionsList.serviceLabel')}
                              {subscription.isGift && (
                                <span className="ml-2 inline-flex items-center gap-1 text-purple-600">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112-2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm0 0V5.5A2.5 2.5 0 1114.5 8H12z" />
                                  </svg>
                                  {t('subscriptionsList.labels.gift')}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action indicator */}
                      <div className="ml-4 flex-shrink-0">
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Section 2: Address */}
                  {subscription.address && (
                    <div className="px-4 pb-3 bg-white">
                      <div className="flex items-center gap-2 text-sm">
                        <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div className="text-gray-700">
                          <span className="font-medium">{subscription.address.street}</span>
                          <span className="text-gray-500"> • {subscription.address.city}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Section 3: Footer - Info badges */}
                  <div className="px-4 py-3 bg-white border-t border-gray-100">
                    <div className="flex flex-wrap gap-2 text-xs">
                      {subscription.nextDeliveryDate && isActive && !isPendingRenewal && (
                        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                          {t('subscriptionsList.labels.next')} {new Date(subscription.nextDeliveryDate).toLocaleDateString()}
                        </span>
                      )}
                      
                      {subscription.isRecurring !== undefined && (
                        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                          {subscription.isRecurring ? t('subscriptionsList.labels.recurring') : t('subscriptionsList.labels.oneTime')}
                        </span>
                      )}
                      
                      {subscription.startDate && (
                        <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                          {t('subscriptionsList.labels.since')} {new Date(subscription.startDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Combined Empty State & Invitation Section */
          <div className="text-center">
            <div className="max-w-md mx-auto">
              {/* Single Long Card */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                {/* Main Message */}
                <div className="mb-6">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Flower className="h-8 w-8 text-amber-400" />
                    <Apple className="h-8 w-8 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {t('subscriptionsList.emptyState.title')}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6 text-sm">
                    {t('subscriptionsList.emptyState.description')}
                  </p>
                </div>
                
                {/* Call to Action */}
                <button
                  onClick={() => navigate('/subscription')}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-4 px-6 rounded-full shadow-md transform transition-all duration-200 hover:scale-[1.02] focus:outline-none mb-4"
                >
                  {t('subscriptionsList.emptyState.button')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
} 